import { memo, useCallback, useState } from 'react';
import { useDispatch } from '@bonanza-social/toolkit/state';
import { useTranslation } from '@bonanza-social/i18n';
import { logError } from '@bonanza-social/toolkit/logging';
import {
  Box,
  Text,
  Icon,
  Button,
  ScrollView,
  UploadImages,
  formatFormDataImage,
  Asset,
} from '@bonanza-social/moonlight';
import {
  MIN_DOCUMENTS_LENGTH,
  MAX_DOCUMENTS_LENGTH,
  CONTENT_CONTAINER_STYLE,
} from './UploadDocuments.content';
import { uploadKYCDocs } from '../../state/onboarding.slice';
import { UploadDocumentsProps } from './UploadDocuments.types';
import { BottomSheet } from '@bonanza-social/navigation';

function UploadDocuments({ onSubmit }: UploadDocumentsProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation('onboarding');
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<Asset[]>([]);

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);

      const data = new FormData();

      for (const document of documents) {
        data.append('Files', formatFormDataImage(document));
      }

      await dispatch(uploadKYCDocs(data)).unwrap();

      onSubmit?.();
    } catch (error) {
      logError('Upload Verification Documents Error', error);
    } finally {
      setLoading(false);
    }
  }, [documents, onSubmit, dispatch]);

  const onDocumentsSizeLimitReached = useCallback(
    (sizeLimit: number, assets: Asset[]) => {
      BottomSheet.open({
        namespace: 'common',
        type: 'InfoImage',
        data: {
          titleNS: 'common',
          titleKey: 'errorTitle',
          description: t('imagesMaxSizeReached', {
            sizeLimit,
            sizeUnit: 'MB',
            count: assets.length,
          }),
        },
      });
    },
    [t],
  );

  return (
    <>
      <ScrollView contentContainerStyle={CONTENT_CONTAINER_STYLE}>
        <Text
          variant="h5"
          locKey="uploadDocumentsTitle"
          locNS="onboarding"
          color="darkSurfacePrimary"
          paddingTop="space-400"
        />
        <UploadImages
          images={documents}
          mediaType="photo"
          sizeLimit={10}
          selectionLimit={MAX_DOCUMENTS_LENGTH - documents.length}
          onChange={setDocuments}
          onSizeLimitReached={onDocumentsSizeLimitReached}
        />
        <Box
          backgroundColor="primaryBackground"
          borderRadius="radius-200"
          padding="space-200"
          gap="space-200"
        >
          <Box flexDirection="row" alignItems="center" gap="space-200">
            <Icon
              name="errorOutline"
              width="space-600"
              height="space-600"
              color="iconSecondary"
            />
            <Text
              variant="caption-semi"
              locKey="uploadDocumentsRequirementsTitle"
              locNS="onboarding"
              color="darkSurfacePrimary"
            />
          </Box>
          <Text
            variant="caption"
            locKey="uploadDocumentsRequirements"
            locNS="onboarding"
            color="darkSurfacePrimary"
            paddingLeft="space-200"
          >
            {t('uploadDocumentsRequirements', {
              minFiles: MIN_DOCUMENTS_LENGTH,
              maxFiles: MAX_DOCUMENTS_LENGTH,
            })}
          </Text>
        </Box>
      </ScrollView>
      <Button
        locKey="submitFiles"
        locNS="onboarding"
        size="large"
        loading={loading}
        disabled={documents.length < MIN_DOCUMENTS_LENGTH}
        onPress={handleSubmit}
      />
    </>
  );
}

export default memo(UploadDocuments);
